// 알림 조건 평가를 위한 공통 유틸리티 함수

/**
 * 알림 조건을 평가하는 함수
 * @param {string} condition - 평가할 조건식 (예: "value > 10", "value == 'ERROR'")
 * @param {any} value - 평가할 값
 * @returns {boolean} 조건 만족 여부
 */
export const evaluateCondition = (condition, value) => {
  if (!condition || value === null || value === undefined) return false;
  
  try {
    // 조건식 파싱을 위한 정규식
    const conditionStr = condition.trim();
    
    // 문자열 비교 (==, !=)
    const stringMatch = conditionStr.match(/^value\s*(==|!=)\s*['"]([^'"]+)['"]$/);
    if (stringMatch) {
      const [, operator, targetValue] = stringMatch;
      const stringValue = String(value);
      return operator === '==' ? stringValue === targetValue : stringValue !== targetValue;
    }
    
    // 숫자 비교 (>=, <=, >, <, ==, !=)
    const numberMatch = conditionStr.match(/^value\s*(>=|<=|>|<|==|!=)\s*(-?\d+(?:\.\d+)?)$/);
    if (numberMatch) {
      const [, operator, targetValueStr] = numberMatch;
      const numValue = Number(value);
      const targetValue = Number(targetValueStr);
      
      if (isNaN(numValue) || isNaN(targetValue)) return false;
      
      switch (operator) {
        case '>': return numValue > targetValue;
        case '>=': return numValue >= targetValue;
        case '<': return numValue < targetValue;
        case '<=': return numValue <= targetValue;
        case '==': return numValue === targetValue;
        case '!=': return numValue !== targetValue;
        default: return false;
      }
    }
    
    return false;
  } catch (err) {
    console.warn('[alertUtils] 조건 평가 오류:', err, '조건:', condition);
    return false;
  }
};

/**
 * 알림 상태를 평가하는 함수
 * @param {any} value - 평가할 값
 * @param {object} conditions - 알림 조건들
 * @param {string} conditions.alertCondition - Warning 조건
 * @param {string} conditions.criticalCondition - Critical 조건
 * @param {string} conditions.emergencyCondition - Emergency 조건
 * @returns {'default'|'warning'|'critical'|'emergency'} 알림 상태
 */
export const getAlertState = (value, conditions) => {
  const { alertCondition, criticalCondition, emergencyCondition } = conditions;
  
  // 우선순위: Emergency > Critical > Warning > Default
  if (emergencyCondition && evaluateCondition(emergencyCondition, value)) {
    return 'emergency';
  }
  if (criticalCondition && evaluateCondition(criticalCondition, value)) {
    return 'critical';
  }
  if (alertCondition && evaluateCondition(alertCondition, value)) {
    return 'warning';
  }
  return 'default';
};

/**
 * 테이블 행에 대한 알림 조건 평가 함수
 * @param {object} row - 테이블 행 데이터
 * @param {string} alertColumn - 평가할 컬럼명
 * @param {object} conditions - 알림 조건들
 * @returns {'default'|'warning'|'critical'|'emergency'} 알림 상태
 */
export const getRowAlertState = (row, alertColumn, conditions) => {
  if (!alertColumn || !row) {
    return 'default';
  }
  
  // row가 배열인 경우와 객체인 경우 모두 처리
  let value;
  if (Array.isArray(row)) {
    // columns 배열에서 alertColumn의 인덱스를 찾아야 함
    // 이 경우 columns 정보가 필요하므로 별도 파라미터로 받아야 함
    return 'default'; // 배열 형태는 별도 처리 필요
  } else {
    // 객체 형태인 경우
    if (!(alertColumn in row)) {
      return 'default';
    }
    value = row[alertColumn];
  }
  
  return getAlertState(value, conditions);
};

/**
 * 테이블 행에 대한 알림 조건 평가 함수 (배열 형태 지원)
 * @param {object|array} row - 테이블 행 데이터
 * @param {array} columns - 컬럼명 배열 (row가 배열인 경우 필요)
 * @param {string} alertColumn - 평가할 컬럼명
 * @param {object} conditions - 알림 조건들
 * @returns {'default'|'warning'|'critical'|'emergency'} 알림 상태
 */
export const getRowAlertStateWithColumns = (row, columns, alertColumn, conditions) => {
  if (!alertColumn || !row) {
    return 'default';
  }
  
  let value;
  if (Array.isArray(row) && Array.isArray(columns)) {
    // row가 배열인 경우, columns에서 alertColumn의 인덱스를 찾음
    const columnIndex = columns.indexOf(alertColumn);
    if (columnIndex === -1 || columnIndex >= row.length) {
      return 'default';
    }
    value = row[columnIndex];
  } else if (typeof row === 'object' && !Array.isArray(row)) {
    // row가 객체인 경우
    if (!(alertColumn in row)) {
      return 'default';
    }
    value = row[alertColumn];
  } else {
    return 'default';
  }
  
  return getAlertState(value, conditions);
};

/**
 * 알림 레벨별 스타일 정보
 */
export const ALERT_STYLES = {
  default: {
    backgroundColor: 'transparent',
    color: 'inherit',
    icon: null,
    animation: null
  },
  warning: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    borderColor: '#ffeaa7',
    icon: '⚠',
    iconBackground: '#ffc107',
    iconColor: '#fff',
    animation: 'warning-pulse'
  },
  critical: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderColor: '#f5c6cb',
    icon: '⚡',
    iconBackground: '#dc3545',
    iconColor: '#fff',
    animation: 'critical-shake'
  },
  emergency: {
    backgroundColor: '#dc3545',
    color: '#ffffff',
    borderColor: '#c82333',
    icon: '🚨',
    iconBackground: '#8b0000',
    iconColor: '#fff',
    animation: 'emergency-intense'
  }
};